/**
 * MobileSupervisionsHome — mobile shell for /supervisions ( <lg ). Batch 6.4.
 * Compact header + KPI strip + 4 sub-tab pills. Renders MobileSupervisions*
 * primitives for Records / Matrix / Actions. Overview reuses the summary
 * KPIs plus a stacked Organisation Policy card. Zero backend changes.
 */
import React from "react";
import {
  ClipboardCheck, Plus, RefreshCw, MoreVertical, Settings2,
  BookOpen, Grid3x3, ListChecks, AlertTriangle, CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import MobileSupervisionsRecordsList from "@/components/mobile/MobileSupervisionsRecordsList";
import MobileSupervisionsMatrixCards from "@/components/mobile/MobileSupervisionsMatrixCards";
import MobileSupervisionsActionsList from "@/components/mobile/MobileSupervisionsActionsList";

const TABS = [
  { key: "overview", label: "Overview", Icon: BookOpen },
  { key: "records",  label: "Records",  Icon: ClipboardCheck },
  { key: "matrix",   label: "Matrix",   Icon: Grid3x3 },
  { key: "actions",  label: "Actions",  Icon: ListChecks },
];

function KpiTile({ label, value, tone, testid, Icon }) {
  const toneCls = {
    red:     "border-red-200 bg-red-50 text-red-800",
    amber:   "border-amber-200 bg-amber-50 text-amber-800",
    sky:     "border-sky-200 bg-sky-50 text-sky-800",
    slate:   "border-slate-200 bg-white text-slate-700",
  }[tone] || "border-slate-200 bg-white text-slate-700";
  return (
    <div data-testid={testid} className={`shrink-0 w-[150px] rounded-lg border px-3 py-2 ${toneCls}`}>
      <div className="text-[9px] uppercase tracking-widest font-semibold opacity-80 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}{label}
      </div>
      <div className="font-heading text-lg font-semibold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

export default function MobileSupervisionsHome({
  tab, setTab,
  records, matrixData, actions, summary,
  settings, settingsDraft, setSettingsDraft, saveSettings, savingSettings,
  canManage, onRefresh, onNew, onOpen, onMark,
}) {
  return (
    <div className="flex-1 min-h-0 flex flex-col bg-slate-50" data-testid="mobile-sup-home">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 shrink-0">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-cyan-50 border border-cyan-100 flex items-center justify-center shrink-0">
            <ClipboardCheck className="w-4 h-4 text-cyan-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">People</div>
            <h1 className="font-heading text-lg font-semibold text-slate-900 truncate" data-testid="mobile-sup-title">
              Supervision &amp; Appraisals
            </h1>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {canManage && (
              <Button size="sm" onClick={onNew}
                data-testid="btn-mobile-sup-new"
                className="h-11 min-h-[44px] bg-cyan-700 hover:bg-cyan-800 text-xs px-3">
                <Plus className="w-4 h-4 mr-1" /> New
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="btn-mobile-sup-more"
                  className="min-h-[44px] w-11 h-11 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
                  aria-label="More actions">
                  <MoreVertical className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={onRefresh} data-testid="btn-mobile-sup-refresh">
                  <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {summary && (
          <div className="mt-3 -mx-4 px-4 overflow-x-auto snap-x snap-mandatory flex gap-2 pb-1" data-testid="mobile-sup-kpi-strip">
            <KpiTile Icon={AlertTriangle} testid="mobile-sup-kpi-overdue" label="Overdue" value={summary.overdue} tone={summary.overdue > 0 ? "red" : "slate"} />
            <KpiTile Icon={CalendarClock} testid="mobile-sup-kpi-due-soon" label="Due Soon" value={summary.due_soon} tone={summary.due_soon > 0 ? "amber" : "slate"} />
            <KpiTile Icon={AlertTriangle} testid="mobile-sup-kpi-never" label="Never" value={summary.never_supervised} tone="slate" />
            <KpiTile Icon={ListChecks} testid="mobile-sup-kpi-actions" label="Open Actions" value={summary.open_actions} tone={summary.open_actions > 0 ? "sky" : "slate"} />
          </div>
        )}
      </div>

      <div className="bg-white border-b border-slate-200 px-4 shrink-0">
        <div className="flex items-center gap-1 overflow-x-auto" data-testid="mobile-sup-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            const Icon = t.Icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                data-testid={`mobile-sup-tab-${t.key}`}
                className={`min-h-[44px] shrink-0 px-3 py-2 text-xs font-medium border-b-2 flex items-center gap-1.5 whitespace-nowrap ${
                  active ? "border-cyan-700 text-cyan-800" : "border-transparent text-slate-500"
                }`}>
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-4 space-y-3">
          {tab === "overview" && (
            <>
              <div className="bg-white border border-slate-200 rounded-md p-3" data-testid="mobile-sup-overview-policy">
                <div className="flex items-center gap-2 mb-2">
                  <Settings2 className="w-4 h-4 text-slate-600" />
                  <div className="text-sm font-semibold">Organisation supervision policy</div>
                  {!canManage && <span className="text-[10px] uppercase tracking-widest text-slate-400 ml-1">read-only</span>}
                </div>
                {settingsDraft && (
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <Label className="text-xs">Organisation name</Label>
                      <Input value={settingsDraft.organisation_name || ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, organisation_name: e.target.value })}
                        disabled={!canManage} data-testid="mobile-sup-settings-org-name" className="h-11" />
                    </div>
                    <div>
                      <Label className="text-xs">Supervisions per year</Label>
                      <Input type="number" min="0" value={settingsDraft.supervisions_per_year}
                        onChange={(e) => setSettingsDraft({ ...settingsDraft, supervisions_per_year: Number(e.target.value) || 0 })}
                        disabled={!canManage} data-testid="mobile-sup-settings-per-year" className="h-11" />
                    </div>
                    <label className="flex items-center gap-2">
                      <Checkbox checked={!!settingsDraft.annual_appraisal} onCheckedChange={(v) => setSettingsDraft({ ...settingsDraft, annual_appraisal: !!v })}
                        disabled={!canManage} data-testid="mobile-sup-settings-annual" />
                      <span className="text-xs">Annual appraisal in addition</span>
                    </label>
                    <div>
                      <Label className="text-xs">Due-soon window (days)</Label>
                      <Input type="number" min="0" value={settingsDraft.due_soon_days}
                        onChange={(e) => setSettingsDraft({ ...settingsDraft, due_soon_days: Number(e.target.value) || 0 })}
                        disabled={!canManage} data-testid="mobile-sup-settings-due-soon" className="h-11" />
                    </div>
                    {canManage && (
                      <Button size="sm" onClick={saveSettings}
                        disabled={savingSettings || JSON.stringify(settings) === JSON.stringify(settingsDraft)}
                        data-testid="btn-mobile-sup-save-settings" className="h-11 bg-cyan-700 hover:bg-cyan-800">
                        {savingSettings ? "Saving…" : "Save policy"}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {tab === "records" && <MobileSupervisionsRecordsList records={records} onOpen={onOpen} />}
          {tab === "matrix"  && <MobileSupervisionsMatrixCards matrixData={matrixData} />}
          {tab === "actions" && <MobileSupervisionsActionsList actions={actions} records={records} canManage={canManage} onMark={onMark} onOpen={onOpen} />}
        </div>
      </div>
    </div>
  );
}
